import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { isAfter, parseISO } from 'date-fns'
import * as R from 'ramda'
import { getLastUpdate } from '../datatime'
import {
  deleteCargo,
  deleteCargoComment,
  getFixtureUpdates,
  getFixtures,
  putCargo,
  putCargoComment,
  putCargoConfirmation,
  sendCargo
} from '../queries'
import { alertsSlice } from './alerts'

export const fixturesSelector = state => state.fixturesSlice

const MAX_FIXTURE_PAGES = 1000
const STREAM_PAGE_SIZE = Math.min(
  5000,
  Math.max(250, Number(import.meta.env.VITE_FIXTURES_PAGE_SIZE || 1500))
)

const normalizeFixtureItems = payload => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.result)) return payload.result
  if (Array.isArray(payload?.Result)) return payload.Result
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.Items)) return payload.Items
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.Data)) return payload.Data
  if (Array.isArray(payload?.rows)) return payload.rows
  if (Array.isArray(payload?.Rows)) return payload.Rows
  if (Array.isArray(payload?.values)) return payload.values
  if (Array.isArray(payload?.Value)) return payload.Value
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

const getFixtureTotalCount = (payload, pageItems) => {
  if (Array.isArray(payload)) return pageItems.length
  const total = Number(
    payload?.totalCount ??
      payload?.TotalCount ??
      payload?.count ??
      payload?.Count ??
      payload?.total ??
      payload?.Total ??
      payload?.recordsCount ??
      payload?.RecordsCount ??
      pageItems.length
  )
  return Number.isFinite(total) ? total : pageItems.length
}

const fetchFixturePages = async ({
  lastUpdate = null,
  getAll = false,
  pageSize = STREAM_PAGE_SIZE,
  onPage = () => {},
  requestId = 'n/a',
  signal
}) => {
  let page = 1
  let totalItems = 0

  while (page <= MAX_FIXTURE_PAGES) {
    if (signal?.aborted) {
      throw new DOMException('Fixtures load aborted', 'AbortError')
    }

    const { data } = await getFixtures({
      lastUpdate,
      getAll,
      page,
      pageSize,
      signal
    })

    if (signal?.aborted) {
      throw new DOMException('Fixtures load aborted', 'AbortError')
    }

    const pageItems = normalizeFixtureItems(data)
    const totalCount = getFixtureTotalCount(data, pageItems)

    const reachedEnd = page * pageSize >= totalCount || pageItems.length === 0

    if (pageItems.length > 0) {
      if (signal?.aborted) {
        throw new DOMException('Fixtures load aborted', 'AbortError')
      }

      totalItems += pageItems.length

      await onPage({ items: pageItems, totalCount, requestId })
      // Yield control so the browser can paint between streamed page inserts.
      await new Promise(resolve => window.setTimeout(resolve, 0))
    }

    if (Array.isArray(data)) {
      break
    }

    if (reachedEnd) {
      break
    }

    page += 1
  }

  return totalItems
}

export const fetchFixtures = createAsyncThunk(
  'fixtures/fetchFixtures',
  async ({ getAll }, { dispatch, requestId, signal }) => {
    dispatch(
      fixturesSlice.actions.startPagedLoad({
        showProgress: getAll,
        requestId
      })
    )

    const totalItems = await fetchFixturePages({
      lastUpdate: null,
      getAll,
      onPage: payload =>
        dispatch(fixturesSlice.actions.upsertMany({ ...payload, requestId })),
      requestId,
      signal
    })

    return { totalItems }
  }
)

export const fetchFixtureUpdates = createAsyncThunk(
  'fixtures/fetchFixtureUpdates',
  async (_, { getState }) => {
    const { lastUpdate } = fixturesSelector(getState())
    if (!lastUpdate) return null

    const { data: remoteLastUpdate } = await getFixtureUpdates()
    if (isAfter(parseISO(remoteLastUpdate), parseISO(lastUpdate))) {
      const items = []
      await fetchFixturePages({
        lastUpdate: remoteLastUpdate,
        getAll: false,
        onPage: pageItems => items.push(...pageItems.items)
      })
      return items
    }
  }
)

export const editFixture = createAsyncThunk(
  'fixtures/editFixture',
  async (input, { dispatch }) => {
    dispatch(fixturesSlice.actions.set(input))
    const { data } = await putCargo({ data: input })
    return data
  }
)

export const removeFixture = createAsyncThunk(
  'fixtures/removeFixture',
  async (input, { dispatch }) => {
    dispatch(fixturesSlice.actions.delete(input))
    const { data } = await deleteCargo({ id: input.id })
    dispatch(
      alertsSlice.actions.push({
        color: 'success',
        message: 'Your fixture has been deleted successfully.'
      })
    )
    return data
  }
)

export const sendFixture = createAsyncThunk(
  'fixtures/sendFixture',
  async ({ row, onlySave = false }) => {
    const { data } = await sendCargo({ data: row, onlySave })
    return data
  }
)

export const confirmFixture = createAsyncThunk(
  'fixtures/confirmFixture',
  async ({ cargo, confirmations, comments, _delete }, { dispatch }) => {
    try {
      await Promise.all([
        ...Object.values(comments).map(data => putCargoComment({ data })),
        ...(_delete?.comments || []).map(data =>
          deleteCargoComment({ id: data.id })
        )
      ])
      if (cargo.status !== 'FIXED') {
        const { data } = await putCargoConfirmation({
          cargoId: cargo.id,
          chartererId: cargo.chartererId,
          data: confirmations
        })
        return data
      }
      return {}
    } catch (err) {
      dispatch(
        alertsSlice.actions.push({
          color: 'danger',
          message: 'An error occurred while confirming this fixture.'
        })
      )
      throw err
    }
  }
)

const initialState = {
  data: null,
  status: 'idle',
  error: null,
  lastUpdate: null,
  currentRequestId: null,
  loadProgress: {
    active: false,
    show: false,
    loaded: 0,
    total: null
  }
}

export const fixturesSlice = createSlice({
  name: 'fixtures',
  initialState,
  reducers: {
    startPagedLoad: (state, action) => {
      const showProgress = Boolean(action.payload?.showProgress)
      state.currentRequestId = action.payload?.requestId || null
      state.data = {}
      state.error = null
      state.status = 'loading'
      state.loadProgress = {
        active: true,
        show: showProgress,
        loaded: 0,
        total: null
      }
    },
    upsertMany: (state, action) => {
      if (
        action.payload?.requestId &&
        state.currentRequestId &&
        action.payload.requestId !== state.currentRequestId
      ) {
        return
      }

      const rows = action.payload?.items || []
      const totalCount = Number(action.payload?.totalCount)
      if (!state.data) {
        state.data = {}
      }
      let added = 0
      rows.forEach(row => {
        if (!state.data[row.id]) added += 1
        state.data[row.id] = row
      })
      if (state.loadProgress.active) {
        state.loadProgress.loaded += added
        if (!Number.isNaN(totalCount) && totalCount >= 0) {
          state.loadProgress.total = totalCount
        }
      }
      // Allow UI table rendering while additional pages are still loading.
      state.status = 'success'
    },
    set: (state, action) => {
      state.data[action.payload.id] = action.payload
    },
    delete: (state, action) => {
      delete state.data[action.payload.id]
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFixtures.pending, (state) => {
      state.status = 'loading'
    })
    builder.addCase(fetchFixtures.rejected, (state, action) => {
      if (action.meta.requestId !== state.currentRequestId) {
        return
      }

      if (action.error?.name === 'AbortError') {
        state.status = 'idle'
        state.error = null
      } else {
        state.status = 'error'
        state.error = action.payload
      }
      state.loadProgress.active = false
      state.currentRequestId = null
    })
    builder.addCase(fetchFixtures.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.currentRequestId) {
        return
      }

      state.status = 'success'
      state.loadProgress.active = false
      state.lastUpdate = getLastUpdate()
      state.currentRequestId = null
    })
    builder.addCase(fetchFixtureUpdates.fulfilled, (state, action) => {
      if (action.payload) {
        state.data = {
          ...state.data,
          ...R.indexBy(R.prop('id'), action.payload)
        }
        state.lastUpdate = getLastUpdate()
      }
    })
    builder.addCase(sendFixture.fulfilled, (state, action) => {
      state.data[action.payload.id] = action.payload
      state.lastUpdate = getLastUpdate()
    })
    builder.addCase(confirmFixture.fulfilled, (state, action) => {
      if (!Array.isArray(action.payload) || action.payload.length === 0) {
        return
      }

      const allConfirmed =
        action.payload.length &&
        action.payload.every(({ confirmed }) => confirmed)
      const oldData = state.data[action.payload[0].cargoId]

      if (!oldData) {
        return
      }

      if (oldData.chartererConfirmed !== allConfirmed) {
        state.data[action.payload[0].cargoId] = {
          ...oldData,
          chartererConfirmed: allConfirmed
        }
        state.lastUpdate = getLastUpdate()
      }
    })
  }
})
